import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from "react";
import { Context, Cookie, Hooks, Util } from "@xt/xtbase";
const { XtContext } = Context;
const { useTranslation } = Hooks;
const { getUrl } = Util;
import { getAssetTrend } from "@/api/xt-report";
import styles from "./index.module.less";
import AssetBalance from "./AssetBalance";
import AssetChart from "./AssetChart";
import HomeQuickActions from "./HomeQuickActions";
import HomeDepositDrawer from "./HomeDepositDrawer";
import HomeCampaignBanner from "./HomeCampaignBanner";
import HomeCoinList from "./HomeCoinList";
import HomeAnnouncement from "./HomeAnnouncement";
import TaskCenter from "../TaskCenter";

const HomeAssetOverview = ({ initialHomeActivity = null, ssrHasToken = false }) => {
  const t = useTranslation();
  const [appState] = React.useContext(XtContext);
  const { loginStatus, currency } = appState;
  /**
   * 水合安全：首屏与服务端 HTML 必须一致，不能用「首 render 读 cookie」区分是否登录。
   * 仅 strict loginStatus===1 视为已登录；鉴权中占位用 SSR 的 ssrHasToken 或与挂载后 cookie 一致。
   */
  const [mounted, setMounted] = useState(false);
  useLayoutEffect(() => {
    setMounted(true);
  }, []);
  const hasTokenCookie = mounted && !!Cookie.get("token");
  const isAuthChecking = loginStatus === 0 && (ssrHasToken || hasTokenCookie);
  const isLogin = loginStatus === 1;

  // 图表展开/收起状态
  const [chartExpanded, setChartExpanded] = useState(false);

  const [homeDepositOpen, setHomeDepositOpen] = useState(false);
  const [homeDepositPreferredFiat, setHomeDepositPreferredFiat] = useState(null);
  const openHomeDeposit = useCallback((preferredFiat) => {
    setHomeDepositPreferredFiat(
      preferredFiat != null && String(preferredFiat).trim() !== ""
        ? String(preferredFiat).toUpperCase()
        : null
    );
    setHomeDepositOpen(true);
  }, []);
  const closeHomeDeposit = useCallback(() => {
    setHomeDepositOpen(false);
    setHomeDepositPreferredFiat(null);
  }, []);

  /** 首页任务：顶部总资产轮询变化时由 TaskCenter 注册刷新 `getActivityList` */
  const homeActivityRefreshRef = useRef(() => {});
  const onTotalUsdtAmountChangedForTasks = useCallback(() => {
    const fn = homeActivityRefreshRef.current;
    if (typeof fn === "function") fn();
  }, []);
  
  // 资产趋势数据（统一在父组件获取，避免收起展开时闪烁）
  const [chartData, setChartData] = useState(null);

  // 获取资产趋势数据（进页面时获取7天数据，用于收起状态显示）
  useEffect(() => {
    if (!isLogin || !currency) {
      setChartData(null);
      return;
    }
    
    const fetchAssetTrend = async () => {
      try {
        const res = await getAssetTrend({
          params: {
            days: 7, // 进页面时获取7天数据
            converts: (currency || "usd").toUpperCase(),
          },
        });
        if (res && Array.isArray(res)) {
          setChartData(res);
        } else {
          setChartData(null);
        }
      } catch (error) {
        console.error("Failed to load asset trend:", error);
        setChartData(null);
      }
    };

    fetchAssetTrend();
  }, [isLogin, currency]);

  return (
    <div className={styles["asset-overview"]}>
      <div className={styles["asset-overview__container"]}>
        <AssetBalance
          isLogin={isLogin}
          isAuthChecking={isAuthChecking}
          chartData={chartData}
          chartExpanded={chartExpanded}
          onToggleChartExpand={() => setChartExpanded(!chartExpanded)}
          onOpenDeposit={openHomeDeposit}
          onTotalUsdtAmountChanged={onTotalUsdtAmountChangedForTasks}
        />
      </div>
      {chartExpanded && (
        <div className={styles["asset-overview__expanded-chart"]}>
          <AssetChart
            isLogin={isLogin}
            chartData={chartData}
            expanded={true}
            onToggleExpand={() => setChartExpanded(false)}
          />
        </div>
      )}
      <TaskCenter
        activityRefreshRef={homeActivityRefreshRef}
        initialActivity={
          initialHomeActivity && (ssrHasToken || isLogin) ? initialHomeActivity : null
        }
      />
      <HomeQuickActions onOpenDeposit={openHomeDeposit} />
      <HomeDepositDrawer
        open={homeDepositOpen}
        onClose={closeHomeDeposit}
        preferredFiatCode={homeDepositPreferredFiat}
      />
      <HomeCampaignBanner />
      <HomeCoinList />
      <HomeAnnouncement />
    </div>
  );
};

export default HomeAssetOverview;
